import { useEffect, useRef, useState } from "react";
import PageContainer from "@components/layout/PageContainer";
import { useParams } from "react-router-dom";
import ModelIndex, { ModelIndexHandle } from "@components/layout/ModelIndex";
import { InvoiceApi, InvoiceDataType } from "@/api/InvoiceApi";
import { InterestEntryApi, InterestEntryDataType } from "@/api/InterestEntryApi";
import { formatMoneyValue } from "@utils/Currency";
import { faEnvelope, faEye, faPrint } from "@fortawesome/free-solid-svg-icons";
import SendDocumentToEmailOffCanvas from "@components/layout/Offcanvas/SendDocumentToEmailOffCanvas";
import { OpenSendDocumentPubSub } from "@utils/pubsubs/PubSub";
import { useAppSelector } from "@/store/hooks";
import moment from "moment";
import { SettlementApi, SettlementDataType } from "@/api/SettlementApi";
import { AmplifyFilter } from "@utils/Api";
import { InterestEntryFormDataKeys } from "@/generated/InterestEntryFormDataKeys";
import { InterestEntryRelatedAttributes } from "@/formTypes/InterestEntryDataType";
import { FetchResponse } from "@/api/ApiClient";

type DocumentInterestListBydocumentIdProps = {
  kind: "invoice" | "settlement"
}

const DocumentInterestListBydocumentId = (props: DocumentInterestListBydocumentIdProps) => {
  const { kind } = props
  const params = useParams()
  const { documentId } = params;
  const modelRef = useRef<ModelIndexHandle>(null);
  const [invoiceData, setInvoiceData] = useState<InvoiceDataType>({});
  const [settlementData, setSettlementData] = useState<SettlementDataType>({});
  const suscriptionData = useAppSelector(state => state.suscription.suscription)

  useEffect(() => {
    if (kind == "invoice") {
      loadInvoice()
    } else {
      loadSettlement()
    }
  }, [documentId]);

  const loadSettlement = async () => {
    if (documentId) {
      const settlement = await SettlementApi.get(documentId);
      setSettlementData(settlement)
    } else {
      alert("No fue posible cargar la informacion de la liquidacion")
    }
  }

  const loadInvoice = async () => {
    if (documentId) {
      const invoice = await InvoiceApi.get(documentId);
      setInvoiceData(invoice)
    } else {
      alert("No fue posible cargar la informacion de la factura")
    }
  }

  const triggerEmailSend = (item: InterestEntryDataType) => {
    OpenSendDocumentPubSub.publish({
      openOffCanvas: true,
      emails: item.settlementInterest.email || "",
      sourceURL: `/user/settlements/print/${item.settlementInterestId}/0/1`,
      emailProps: {
        emailBody: suscriptionData?.settlement_email || "",
        emailFilename: `Liquidacion #${item.settlementInterest.numero}`,
        emailSubject: `Liquidacion #${item.settlementInterest.numero}`,
        Suscription: {
          email: suscriptionData?.email || ""
        }
      }
    })
  }

  const viewSettlement = (interestEntry: InterestEntryDataType) => {
    window.open(`/user/settlements/view/${interestEntry.settlementInterestId}`)
  }

  const printSettlement = (interestEntry: InterestEntryDataType) => {
    window.open(`/user/settlements/print/${interestEntry.settlementInterestId}/0/1`)
  }

  const fetchInterest = async (amplifyClientFilter?: AmplifyFilter, token?: string, limit = 20): Promise<FetchResponse<InterestEntryDataType>> => {
    return InterestEntryApi.fetchAll(amplifyClientFilter, token, limit,
      [
        ...InterestEntryFormDataKeys.filter(k => !InterestEntryRelatedAttributes.includes(k)),
        "currency.*",
        "settlementInterest.*"
      ])
  }

  return (
    <PageContainer
      title={`Intereses Generados de ${kind == "invoice" ? `Factura #${invoiceData.numero || ""} ` : `Liquidacion #${settlementData.numero || ""}`}`}
      backUrl="/user/dashboard-accounting"
    >
      <ModelIndex<InterestEntryDataType>
        ref={modelRef}
        messages={{
          noResults: "No hay registro de ninguna liquidacion por intereses para este documento"
        }}
        mandatoryFilters={[{ [`${kind}Id`]: { eq: documentId } }]}
        fetchData={(...args) => fetchInterest(...args)}
        primaryProperties={[
          {
            key: "numero",
            highlighted: true,
          },
          {
            key: (d) => { return d.settlementInterest?.nombre_factura },
            alignment: "Start"
          },
        ]}
        primaryPropertiesSizing={
          [
            { sm: "2", xs: "12" },
            { sm: "10", xs: "12" }
          ]
        }
        secondaryProperties={[
          {
            key: (data) => {
              const date = moment(data.created, "YYYY-MM-DD")
              return date.format("DD-MM-YYYY")
            },
            title: "Fecha",
          },
          {
            key: (data) => {
              return data.days
            },
            title: "Dias de Intereses"
          },
          {
            key: (data) => {
              return formatMoneyValue(data.amount, data.currency.currency_id)
            },
            title: "Intereses",
            highlighted: true
          }
        ]}
        secondaryPropertiesSizing={
          [
            { sm: "4", md: "2" },
            { sm: "4", md: "3" },
            { sm: "4", md: "2" }
          ]
        }
        listActions={[
          {
            onClick: (id, item) => triggerEmailSend(item),
            actionTitle: "Enviar Por Correo ",
            iconProps: { icon: faEnvelope }
          },
          {
            onClick: (id, item) => viewSettlement(item),
            actionTitle: "Ver Liquidacion de Intereses",
            iconProps: { icon: faEye }
          },
          {
            onClick: (id, item) => printSettlement(item),
            actionTitle: "Imprimir Liquidacion de intereses",
            iconProps: { icon: faPrint }
          }
        ]}
      >
      </ModelIndex>
      <SendDocumentToEmailOffCanvas
        title="Envio de Liquidacion a Email" />

    </PageContainer >
  );
}

export default DocumentInterestListBydocumentId;
